import React, { useEffect, useState, useContext } from "react";
import { useParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { Card, Table, Button, message, Tag, Space, Modal, Select, Divider } from "antd";
import { TeamOutlined } from "@ant-design/icons";
import { fetchMatchById as fetchMatchDetails } from "../api/services/matchfetchService";
import { createFrame as addFrameToMatch } from "../api/services/frameService";
import { finishMatch } from "../api/services/matchService";
import { getPlayers } from "../api/services/playerService";
import { useUpdateMatch } from "../hooks/useMatches";
import EditMatchModal from "../components/Match/EditMatchModal";
import AddFrameModal from "../components/Frame/AddFrameModal";
import { AuthContext } from "../context/AuthContext";
import "./MatchDetails.css"; // CSS-Datei für die Tabelle

const MatchDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams(); // Match-ID aus der URL
  const { currentUser } = useContext(AuthContext);
  const [match, setMatch] = useState(null);
  const [frames, setFrames] = useState([]);
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [isFinishModalVisible, setIsFinishModalVisible] = useState(false);
  const [selectedWinner, setSelectedWinner] = useState(null);
  const [finishLoading, setFinishLoading] = useState(false);
  const updateMatchMutation = useUpdateMatch();

  // Match-Daten laden
  const loadMatchDetails = async () => {
    try {
      setLoading(true);
      const data = await fetchMatchDetails(id); // API-Aufruf für Match-Details
      console.log("Match details:", data);
      setMatch(data);
      setFrames(data.frames);
      const playerData = await getPlayers();
      setPlayers(playerData);
      console.log("Player data:", playerData);
    } catch (error) {
      message.error("Fehler beim Laden der Match-Details.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(
    () => {
      loadMatchDetails();
    },
    [id]
  );

  const getPlayerNameById = id => {
    const player = players.find(player => player.id === id);
    return player ? player.name : "Unbekannt";
  };

  // Issue #29: Match beenden Handler
  const handleFinishMatch = async () => {
    try {
      setFinishLoading(true);

      // API-Call zum Beenden des Matches
      await finishMatch(id, selectedWinner);

      message.success("Match erfolgreich beendet!");
      setIsFinishModalVisible(false);
      setSelectedWinner(null);
      loadMatchDetails(); // Daten neu laden
    } catch (error) {
      console.error("Error finishing match:", error);
      const errorMessage = error.response?.data?.detail
        || error.message
        || "Fehler beim Beenden des Matches";
      message.error(errorMessage);
    } finally {
      setFinishLoading(false);
    }
  };

  // Issue #29: Finish-Dialog öffnen
  const openFinishDialog = () => {
    if (match.is_team_match) {
      // Bei Team-Matches: Team mit mehr Frames als Default-Winner setzen
      const team1Score = match.team1_score || 0;
      const team2Score = match.team2_score || 0;
      if (team1Score > team2Score) {
        setSelectedWinner('team1');
      } else if (team2Score > team1Score) {
        setSelectedWinner('team2');
      } else {
        setSelectedWinner(null);
      }
    } else {
      // Auto-Detection: Spieler mit mehr Frames als Default-Winner setzen
      if (match.player1_score > match.player2_score) {
        setSelectedWinner(match.player1);
      } else if (match.player2_score > match.player1_score) {
        setSelectedWinner(match.player2);
      } else {
        setSelectedWinner(null); // Bei Gleichstand muss User wählen
      }
    }
    setIsFinishModalVisible(true);
  };
  // Match aktualisieren
  const handleUpdateMatch = (matchId, matchData) => {
    updateMatchMutation.mutate({ matchId, matchData }, {
      onSuccess: () => {
        message.success("Match erfolgreich aktualisiert!");
        setIsEditModalVisible(false);
        loadMatchDetails(); // Daten neu laden
      },
      onError: (error) => {
        const errorMessage = error.response?.data?.detail
          || error.message
          || "Fehler beim Aktualisieren des Matches";
        message.error(errorMessage);
      },
    });
  };

  // Frame hinzufügen - bekommt fertige Daten vom Modal
  const handleAddFrame = async (frameData) => {
    try {
      // Validate match ID
      if (!id) {
        console.error("Match ID is missing!");
        message.error("Match-ID fehlt. Bitte versuchen Sie es erneut.");
        return;
      }

      // Calculate loser based on match type
      let loser;
      if (match.is_team_match) {
        // Bei Team-Matches: Winner ist aus einem Team, Loser ist ein Spieler aus dem anderen Team
        const team1PlayerIds = match.team1_players?.map(p => p.id) || [];
        const team2PlayerIds = match.team2_players?.map(p => p.id) || [];

        if (team1PlayerIds.includes(frameData.winner)) {
          // Winner aus Team 1 -> Loser aus Team 2 (erster Spieler als Default)
          loser = team2PlayerIds[0];
        } else {
          // Winner aus Team 2 -> Loser aus Team 1 (erster Spieler als Default)
          loser = team1PlayerIds[0];
        }
      } else {
        // Bei Einzel-Matches: der andere Spieler ist der Loser
        loser = frameData.winner === match.player1
          ? match.player2
          : match.player1;
      }

      // Build complete frame data with match ID
      const completeFrameData = {
        winner: frameData.winner,
        loser: loser,
        match: id,
        loser_remaining_balls: frameData.loser_remaining_balls ?? 0,
        eight_ball_flag: frameData.eight_ball_flag ?? false,
        won_on_break: frameData.won_on_break ?? false,
        // Feature #24: Break-Tracking
        breaking_player: frameData.breaking_player ?? null,
        // Feature #23: Foul-Tracking
        winner_fouls: frameData.winner_fouls ?? 0,
        loser_fouls: frameData.loser_fouls ?? 0
      };

      console.log("Sending frame data to API:", completeFrameData);
      console.log("Match ID:", id);

      const data = await addFrameToMatch(completeFrameData);
      console.info("Frame successfully created:", data);

      message.success("Frame erfolgreich hinzugefügt!");

      setIsModalVisible(false);
      loadMatchDetails(); // Daten neu laden
    } catch (error) {
      console.error("Error adding frame:", error);
      console.error("Error response:", error.response?.data);

      const errorMessage = error.response?.data?.detail
        || error.response?.data?.match?.[0]
        || error.message
        || "Fehler beim Hinzufügen des Frames";
      message.error(errorMessage);
    }
  };

  const columns = [
    {
      title: "Winner",
      dataIndex: "winner",
      key: "winner",
      render: winnerId => getPlayerNameById(winnerId) // Gewinnername ermitteln
    },
    {
      title: "Loser",
      dataIndex: "loser",
      key: "loser",
      render: loserId => getPlayerNameById(loserId) // Verlierername ermitteln
    },
    {
      title: "Verbleibende Bälle",
      dataIndex: "loser_remaining_balls",
      key: "loser_remaining_balls",
      render: text => (text !== null ? text : "-")
    },
    {
      title: "Nur 8 am Tisch",
      dataIndex: "eight_ball_flag",
      key: "eight_ball_flag",
      render: text => (text ? "Ja" : "Nein")
    },
    {
      title: "Break & Run",
      dataIndex: "won_on_break",
      key: "won_on_break",
      render: text => (text ? <Tag color="green">Ja</Tag> : <Tag>Nein</Tag>)
    }
  ];

  // Berechtigungsprüfung: Darf der Benutzer dieses Match bearbeiten?
  const canEditMatch = () => {
    if (!currentUser || !match) return false;

    // Staff oder Superuser dürfen immer bearbeiten
    if (currentUser.is_staff || currentUser.is_superuser) return true;

    // Bei Team-Matches: Prüfen ob User in einem der Teams ist
    if (match.is_team_match) {
      const allPlayerIds = match.all_players?.map(p => p.id) || [];
      return allPlayerIds.includes(currentUser.player_id);
    }

    // Benutzer muss am Match beteiligt sein (player1 oder player2)
    return currentUser.player_id === match.player1 || currentUser.player_id === match.player2;
  };

  // Helper für Team-Match Score-Berechnung
  const getMaxScore = () => {
    if (match.is_team_match) {
      return Math.max(match.team1_score || 0, match.team2_score || 0);
    }
    return Math.max(match.player1_score, match.player2_score);
  };

  if (loading) {
    return <p>Loading...</p>;
  }

  return (
    <div style={{ padding: "20px" }}> {/* Padding für die gesamte Ansicht */}
      <Card
        title={
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <span>Match-Details</span>
            {match.is_team_match && <Tag color="cyan" icon={<TeamOutlined />}>Team-Match</Tag>}
            {match.is_open_end && <Tag color="blue">Open End</Tag>}
            {match.is_practice_match && <Tag color="purple">Übung</Tag>}
          </div>
        }
        bordered={true}
        style={{ marginBottom: "20px" }} // Abstand nach unten
      >
        {/* Team-Match Anzeige */}
        {match.is_team_match ? (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
              {/* Team 1 */}
              <div style={{ flex: 1, textAlign: 'center' }}>
                <strong style={{ fontSize: '18px' }}>{match.team1_name || 'Team 1'}</strong>
                <div style={{ fontSize: '32px', fontWeight: 'bold', margin: '8px 0' }}>
                  {match.team1_score || 0}
                </div>
                <div style={{ fontSize: '12px', color: '#888' }}>
                  {match.team1_players?.map(p => p.name).join(', ') || '-'}
                </div>
              </div>

              {/* VS */}
              <div style={{ padding: '0 20px', alignSelf: 'center' }}>
                <strong style={{ fontSize: '20px', color: '#888' }}>VS</strong>
              </div>

              {/* Team 2 */}
              <div style={{ flex: 1, textAlign: 'center' }}>
                <strong style={{ fontSize: '18px' }}>{match.team2_name || 'Team 2'}</strong>
                <div style={{ fontSize: '32px', fontWeight: 'bold', margin: '8px 0' }}>
                  {match.team2_score || 0}
                </div>
                <div style={{ fontSize: '12px', color: '#888' }}>
                  {match.team2_players?.map(p => p.name).join(', ') || '-'}
                </div>
              </div>
            </div>
            <Divider />
          </>
        ) : (
          <>
            <p>
              <strong>Spieler:</strong> {match.player1_name} vs.{" "}
              {match.player2_name}
            </p>
            <p>
              <strong>Punkte:</strong> {match.player1_score} - {match.player2_score}
            </p>
          </>
        )}
        <p>
          <strong>Spiel:</strong> {match.game_type_name}
        </p>
        {!match.is_open_end && (
          <p>
            <strong>Best of:</strong> {match.best_of} ||  <strong>Race To:</strong> {match.race_to}
          </p>
        )}
        <p>
          <strong>Gespielte Frames:</strong> {frames.length}
        </p>
        {!match.is_open_end && (
          <p>
            <strong>Offene Frames:</strong>{" "}
            {Math.max(0, match.race_to - getMaxScore())}
          </p>
        )}
        </Card>
       

      <Space
        wrap
        style={{
          marginBottom: "20px",
          width: "100%",
          justifyContent: "center",
          display: "flex"
        }}
        size="small"
      >
        {canEditMatch() && !match.is_finished && (match.is_open_end || Math.max(0, match.race_to - getMaxScore()) > 0) && (
          <Button type="primary" onClick={() => setIsModalVisible(true)}>
            Frame hinzufügen
          </Button>
        )}
        {canEditMatch() && !match.is_finished && (
          <Button onClick={() => setIsEditModalVisible(true)}>
            Match bearbeiten
          </Button>
        )}
        {/* Issue #29: Match beenden Button - nur für Open-End Matches */}
        {canEditMatch() && match.is_open_end && !match.is_finished && (
          <Button type="default" onClick={openFinishDialog} style={{ backgroundColor: '#52c41a', color: 'white' }}>
            Match beenden
          </Button>
        )}
        <Button
          danger
          type="primary"
          onClick={() => navigate("/dashboard")}
          style={{ fontWeight: 'bold' }}
        >
          Zurück zur Übersicht
        </Button>
      </Space>
      
      <Table
        dataSource={frames}
        columns={columns}
        rowKey={record => record.id}
        pagination={false} // Paging deaktivieren
        style={{ marginTop: "20px" }}
        scroll={{ y: 400 }}
        footer={() =>
          <div>
            Frameanzahl: {frames.length}
          </div>}
      />

      <EditMatchModal
        visible={isEditModalVisible}
        onClose={() => setIsEditModalVisible(false)}
        onUpdateMatch={handleUpdateMatch}
        match={match}
      />

      <AddFrameModal
        visible={isModalVisible}
        onClose={() => setIsModalVisible(false)}
        onAddFrame={handleAddFrame}
        players={match.is_team_match
          ? match.all_players || []
          : [
              { id: match.player1, name: match.player1_name },
              { id: match.player2, name: match.player2_name }
            ]
        }
        isEightBall={match.game_type_name === "8-Ball"}
        isTeamMatch={match.is_team_match || false}
        team1Players={match.team1_players || []}
        team2Players={match.team2_players || []}
        team1Name={match.team1_name}
        team2Name={match.team2_name}
      />

      {/* Issue #29: Match beenden Dialog */}
      <Modal
        title="Match beenden"
        open={isFinishModalVisible}
        onOk={handleFinishMatch}
        onCancel={() => {
          setIsFinishModalVisible(false);
          setSelectedWinner(null);
        }}
        okText="Match beenden"
        cancelText="Abbrechen"
        confirmLoading={finishLoading}
        okButtonProps={{
          disabled: !selectedWinner
        }}
      >
        <p><strong>Aktueller Stand:</strong></p>
        {match.is_team_match ? (
          <>
            <p>{match.team1_name || 'Team 1'}: {match.team1_score || 0} Frames</p>
            <p>{match.team2_name || 'Team 2'}: {match.team2_score || 0} Frames</p>
          </>
        ) : (
          <>
            <p>{match.player1_name}: {match.player1_score} Frames</p>
            <p>{match.player2_name}: {match.player2_score} Frames</p>
          </>
        )}
        <br />
        <p><strong>Gewinner auswählen:</strong></p>
        <Select
          value={selectedWinner}
          onChange={(value) => setSelectedWinner(value)}
          style={{ width: '100%' }}
          placeholder="Gewinner auswählen"
        >
          {match.is_team_match ? (
            <>
              <Select.Option value="team1">
                {match.team1_name || 'Team 1'} ({match.team1_score || 0} Frames)
              </Select.Option>
              <Select.Option value="team2">
                {match.team2_name || 'Team 2'} ({match.team2_score || 0} Frames)
              </Select.Option>
            </>
          ) : (
            <>
              <Select.Option value={match.player1}>
                {match.player1_name} ({match.player1_score} Frames)
              </Select.Option>
              <Select.Option value={match.player2}>
                {match.player2_name} ({match.player2_score} Frames)
              </Select.Option>
            </>
          )}
        </Select>
        {((match.is_team_match && (match.team1_score || 0) === (match.team2_score || 0)) ||
          (!match.is_team_match && match.player1_score === match.player2_score)) && (
          <p style={{ color: '#faad14', marginTop: '10px' }}>
            Gleichstand: Gewinner muss manuell ausgewählt werden.
          </p>
        )}
      </Modal>
    </div>
  );
};

export default MatchDetails;
